import { languageName, type BotLanguages } from "@kickwin/shared";
import { cn } from "@/lib/utils";

/**
 * Which language the editor below is showing (decision 46). The default is
 * the message itself; the others only translate its words. Hidden when the
 * bot has a single language.
 */
export function LanguageTabs({
  languages,
  value,
  onChange,
  translated,
}: {
  languages: BotLanguages | undefined;
  /** null: the default language. */
  value: string | null;
  onChange: (lang: string | null) => void;
  /** Languages that already have some words, marked with a dot. */
  translated?: (lang: string) => boolean;
}) {
  if (!languages || languages.extra.length === 0) return null;
  const tabs: { lang: string | null; label: string }[] = [
    { lang: null, label: `${languageName(languages.default)} · default` },
    ...languages.extra.map((lang) => ({ lang, label: languageName(lang) })),
  ];
  return (
    <div
      role="tablist"
      aria-label="Language"
      className="-mx-1 flex gap-1 overflow-x-auto px-1 pb-0.5 [scrollbar-width:thin]"
    >
      {tabs.map((tab) => {
        const selected = tab.lang === value;
        return (
          <button
            key={tab.lang ?? "default"}
            type="button"
            role="tab"
            aria-selected={selected}
            onClick={() => onChange(tab.lang)}
            className={cn(
              "flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1 text-[13px] whitespace-nowrap transition-colors [@media(pointer:coarse)]:py-1.5",
              selected
                ? "border-primary bg-primary text-primary-foreground"
                : "border-border bg-card hover:border-ring/60",
            )}
          >
            {tab.label}
            {tab.lang && translated?.(tab.lang) && (
              <span
                aria-label="has translations"
                className={cn(
                  "size-1.5 rounded-full",
                  selected ? "bg-primary-foreground" : "bg-primary",
                )}
              />
            )}
          </button>
        );
      })}
    </div>
  );
}

/** The note shown above a translation: what changes and what does not. */
export function TranslationNote({ lang }: { lang: string }) {
  return (
    <p className="rounded-md bg-accent px-3 py-2 text-[13px] text-accent-foreground">
      Translating into {languageName(lang)}. Buttons, links and media are shared with the default;
      only words change here. Anything left empty uses the default text.
    </p>
  );
}
